/* global BigInt */
// Pure sale-unit <-> base-unit price conversion for the Add customer price drawer. BigInt fixed point,
// never floats. Mirrors the server contract: base = sale / ratio at 12 dp half-up; sale = base * ratio (exact).
const SCALE = 12n;
const ONE = 10n ** SCALE;
const INPUT = /^\d+(\.\d+)?$/;

// Accepts a comma decimal (FR keyboard). Returns null for empty / invalid / more than 12 dp.
export function parseFixed(text) {
  const s = String(text ?? '').trim().replace(',', '.');
  if (!INPUT.test(s)) return null;
  const [w, f = ''] = s.split('.');
  return f.length > 12 ? null : BigInt(w) * ONE + BigInt(f.padEnd(12, '0') || '0');
}

// scaled (value x 10^scale) -> trimmed dot-decimal text
function toText(v, scale) {
  const raw = v.toString().padStart(Number(scale) + 1, '0');
  const w = raw.slice(0, raw.length - Number(scale)); const f = raw.slice(raw.length - Number(scale)).replace(/0+$/, '');
  return f ? `${w}.${f}` : w;
}

const divHalfUp = (n, d) => (n + d / 2n) / d;

// typed: price text as typed; unit: 'sale' | 'base'; ratio: text.
// -> { other: exact text of the other unit's price (rounded to 12 dp if it cannot be exact), rounded: bool } | null
export function convertPrice({ typed, unit, ratio }) {
  const p = parseFixed(typed); const r = parseFixed(ratio);
  if (p == null || r == null || r <= 0n) return null;
  if (unit === 'sale') {
    const base = divHalfUp(p * ONE, r);                     // base @12dp
    return { other: toText(base, SCALE), rounded: base * r !== p * ONE };
  }
  const exact = p * r;                                      // sale @24dp
  const sale = divHalfUp(exact, ONE);                       // sale @12dp
  return { other: toText(sale, SCALE), rounded: sale * ONE !== exact };
}

// Display only: exact dot-decimal text rounded half-up to `dp` places, zeros trimmed (the caller keeps the exact text for the tooltip).
export function roundText(text, dp = 6) {
  const v = parseFixed(text);
  if (v == null) return '';
  const drop = 10n ** BigInt(12 - dp);
  return toText(divHalfUp(v, drop), BigInt(dp));
}

// 47080 (R5b): the QUANTITY twin of the server's lib/saleUnit (saleQtyToBase: base = qty x ratio, refused off the base grid; never rounded).
// typed: quantity text in `unit` ('sale' | 'base'); ratio: the line's frozen sale-unit ratio; baseDp: decimals the base quantity may carry (item rule, else 2).
// -> { base, sale, saleExact, wholeSale, baseOk } (exact texts; `sale` rounded to 6 dp only when it cannot be exact) | null when not a number
export function convertQty({ typed, unit, ratio, baseDp }) {
  const q = parseFixed(typed); const r = parseFixed(ratio);
  if (q == null || r == null || r <= 0n) return null;
  const base24 = unit === 'sale' ? q * r : q * ONE;                      // base @24dp (exact)
  const saleExact = unit === 'sale' || (q * ONE) % r === 0n;
  const sale12 = unit === 'sale' ? q : (q * ONE) / r;                    // sale @12dp (truncated when inexact; then shown rounded)
  const dp = BigInt(Math.max(0, Math.min(12, Number(baseDp ?? 2))));   // null / undefined = no item rule: the 0.01 grid
  return {
    base: toText(base24, 2n * SCALE),
    sale: saleExact ? toText(sale12, SCALE) : roundText(toText(divHalfUp(q * ONE, r), SCALE), 6),
    saleExact,
    wholeSale: saleExact && sale12 % ONE === 0n,
    baseOk: base24 % (10n ** (2n * SCALE - dp)) === 0n,
  };
}
